import chunk from 'lodash/chunk'

import {
    getErrorMessage,
    getErrorHeader,
    getErrorReason,
    getMembersNames,
    formatPlayer,
    processReponseReasonArray,
    wrapInP,
    BASE_ERRORS,
    BASE_ERROR_HEADERS,
} from './base'
import { MESSAGE_PLAYERS_CHUNK } from './constants'

import { getLocalizedName, QUARTERMASTER } from 'utils/clan_management/roles'
import { formatMessage } from 'locale'
import messages from './translations'

export const REMOVE_MEMBERS_SUCCESS = 'NOTIFICATIONS_REMOVE_MEMBERS_SUCCESS'
export const REMOVE_MEMBERS_ERROR = 'NOTIFICATIONS_REMOVE_MEMBERS_ERROR'

export const CHANGE_ROLE_SUCCESS = 'NOTIFICATIONS_CHANGE_ROLE_SUCCESS'
export const CHANGE_ROLE_ERROR = 'NOTIFICATIONS_CHANGE_ROLE_ERROR'

const rankTooLowMessage = (names) => (
    formatMessage(messages.rank_too_low, {
        names: names.map((memberName) => formatPlayer(memberName)).join(', '),
    })
)

const treasurersMessage = (names) => (
    formatMessage(messages.treasurers, {
        names: names.map((memberName) => formatPlayer(memberName)).join(', '),
    })
)

const notInClanMessage = (names) => (
    formatMessage(messages.members_not_in_clan, {
        names: names.map((memberName) => formatPlayer(memberName)).join(', '),
    })
)

export const getMessagesCannotDoAction = (info) => {
    const higherRankIds = []
    const treasurersIds = []

    if (info.reason_rank_too_high_accounts) {
        Object.keys(info.reason_rank_too_high_accounts).forEach( id => {
            const role = info.reason_rank_too_high_accounts[id]
            if (role === QUARTERMASTER) treasurersIds.push(id)
            else higherRankIds.push(id)
        })
    }

    const higherRankNames = getMembersNames(higherRankIds, info.members)
    const treasurerNames = getMembersNames(treasurersIds, info.members)

    let messagesSequence = []
    let temporalChunk = []
    let temporalChunkNamesLength = 0

    if (higherRankNames.length) {
        const processed = processReponseReasonArray(
            temporalChunk, temporalChunkNamesLength, higherRankNames, messagesSequence, info, rankTooLowMessage
        )
        temporalChunkNamesLength = processed.tempLength
        temporalChunk = processed.temp
    }
    if (treasurerNames.length) {
        const processed = processReponseReasonArray(
            temporalChunk, temporalChunkNamesLength, treasurerNames, messagesSequence, info, treasurersMessage
        )
        temporalChunkNamesLength = processed.tempLength
        temporalChunk = processed.temp
    }
    if (info.reason_not_in_clan_ids.length) {
        const names = getMembersNames(info.reason_not_in_clan_ids, info.members)
        const processed = processReponseReasonArray(
            temporalChunk, temporalChunkNamesLength, names, messagesSequence, info, notInClanMessage
        )
        temporalChunkNamesLength = processed.tempLength
        temporalChunk = processed.temp
    }

    if (temporalChunkNamesLength) {
        messagesSequence.push(temporalChunk)
    }

    return messagesSequence.map((message) => Array.isArray(message) ? wrapInP(...message) : message)
}

export const removeMemberSuccessNotification = (playerNames) => {
    return (dispatch) => chunk(playerNames, MESSAGE_PLAYERS_CHUNK()).map((playerNamesChunk) =>
        dispatch(removeMemberSuccessSingleNotification(playerNamesChunk))
    )
}

export const removeMemberSuccessSingleNotification = (playerNames) => ({
    type: REMOVE_MEMBERS_SUCCESS,
    payload: {},
    meta: {
        webToClient: {
            handler: 'sendNotification',
            params: {
                type: 'Warning',
                header: formatMessage(messages.remove_members_success_header),
                message: formatMessage(messages.remove_members_success_text, {
                    names: playerNames.map((name) => formatPlayer(name)).join(', '),
                }),
            },
        },
    },
})

const REMOVE_MEMBERS_ERRORS = {
    ...BASE_ERRORS,
    cannot_remove_members: getMessagesCannotDoAction,
    'default': () => formatMessage(messages.remove_members_server_error),
}

export const removeMemberErrorNotification = (error = {}, accountId, members) => {
    return (dispatch) => {
        const reason = getErrorReason(error)
        const extra = {
            ...error.additional_data,
            accountId,
            members,
        }

        const messagesSequence = getErrorMessage(REMOVE_MEMBERS_ERRORS, reason, extra)

        return Array.isArray(messagesSequence) ?
            messagesSequence.map((message) => dispatch(removeMemberErrorSingleNotification(reason, message))) :
            dispatch(removeMemberErrorSingleNotification(reason, messagesSequence))
    }
}

const REMOVE_MEMBERS_ERROR_HEADERS = {
    ...BASE_ERROR_HEADERS,
    cannot_remove_members: () => formatMessage(messages.remove_members_cannot_remove),
}

export const removeMemberErrorSingleNotification = (reason, message) => ({
    type: REMOVE_MEMBERS_ERROR,
    payload: {},
    meta: {
        webToClient: {
            handler: 'sendNotification',
            params: {
                type: 'ErrorSimple',
                header: getErrorHeader(REMOVE_MEMBERS_ERROR_HEADERS, reason),
                message,
            },
        },
    },
})

export const changeRoleSuccessNotification = (roleName, playerNames) => {
    return (dispatch) => chunk(playerNames, MESSAGE_PLAYERS_CHUNK()).map((playerNamesChunk) =>
        dispatch(changeRoleSuccessSingleNotification(roleName, playerNamesChunk))
    )
}

export const changeRoleSuccessSingleNotification = (roleName, playerNames) => ({
    type: CHANGE_ROLE_SUCCESS,
    payload: {},
    meta: {
        webToClient: {
            handler: 'sendNotification',
            params: {
                type: 'Warning',
                header: formatMessage(messages.change_role_success_header),
                message: formatMessage(messages.change_role_success_text, {
                    role: getLocalizedName(roleName),
                    names: playerNames.map((name) => formatPlayer(name)).join(', '),
                }),
            },
        },
    },
})

export const leavingFromClanSuccessNotification = () => (dispatch, getState) => {
    dispatch({
        type: CHANGE_ROLE_SUCCESS,
        payload: {},
        meta: {
            webToClient: {
                handler: 'sendNotification',
                params: {
                    type: 'Warning',
                    message: formatMessage(messages.leave_clan_success_text, {
                        hours: Math.round(getState().settings.clanEntranceCooldown / 3600),
                    }),
                },
            },
        },
    })
}

const CHANGE_ROLE_ERRORS = {
    ...BASE_ERRORS,
    cannot_change_role: getMessagesCannotDoAction,
    'default': () => formatMessage(messages.change_role_server_error),
}

const CHANGE_ROLE_ERROR_HEADERS = {
    ...BASE_ERROR_HEADERS,
    cannot_change_role: () => formatMessage(messages.change_role_cannot_change_role),
}

export const changeRoleErrorNotification = (error = {}, accountId, members) => {
    return (dispatch) => {
        const reason = getErrorReason(error)
        const extra = {
            ...error.additional_data,
            accountId,
            members,
        }

        const messagesSequence = getErrorMessage(CHANGE_ROLE_ERRORS, reason, extra)

        return Array.isArray(messagesSequence) ?
            messagesSequence.map((message) => dispatch(changeRoleErrorSingleNotification(reason, message))) :
            dispatch(changeRoleErrorSingleNotification(reason, messagesSequence))
    }
}

export const changeRoleErrorSingleNotification = (reason, message) => ({
    type: CHANGE_ROLE_ERROR,
    payload: {},
    meta: {
        webToClient: {
            handler: 'sendNotification',
            params: {
                type: 'ErrorSimple',
                header: getErrorHeader(CHANGE_ROLE_ERROR_HEADERS, reason),
                message,
            },
        },
    },
})



// WEBPACK FOOTER //
// ./state/actions/CMT/Notifications/MemberManagement.js